import { SNOMED } from '@medplum/core';
import { CodeableConcept, Coding, Communication, Identifier } from '@medplum/fhirtypes';
import {
  COVERAGE_CLASS_CODESYSTEM,
  GROUP_CLASS_CODE,
  INSURER_ORGANIZATION_TYPE_CODE,
  ORGANIZATION_TYPE_CODESYSTEM,
  type SubscriberRelationship,
} from './insurance-coverage';
import {
  BariatricProcedureStage,
  BARIENDO_CT_SYSTEM,
  CommunicationCategory,
  CommunicationDirection,
  CommunicationForwardCategory,
  CommunicationMedium,
  CommunicationThreadTopic,
  COMMUNICATION_THREAD_TOPIC_PATIENT_LABELS,
  CONFIDENTIALITY_CODESYSTEM,
  ConfidentialityLevel,
  FlagCategory,
  IVR_BASIC_SYSTEM,
  LocationType,
  OrganizationType,
  PARTICIPATION_MODE_CODESYSTEM,
  ProvenanceReason,
  ROLE_CODE_CODESYSTEM,
  ProviderAvailabilityStatus,
  ProviderRole,
  PROVIDER_ROLE_LABELS,
  ScheduleCategory,
  TaskGroupIdentifier,
  SmsReason,
  SMS_REASON_SYSTEM,
} from './constants';

export const ADMINISTRATOR_PROVIDER_ROLE: Coding = {
  system: SNOMED,
  code: ProviderRole.ADMINISTRATOR,
  display: PROVIDER_ROLE_LABELS.ADMINISTRATOR,
};

export const PATIENT_COORDINATOR_PROVIDER_ROLE: Coding = {
  system: SNOMED,
  code: ProviderRole.PATIENT_COORDINATOR,
  display: PROVIDER_ROLE_LABELS.PATIENT_COORDINATOR,
};

export const PATIENT_NAVIGATOR_PROVIDER_ROLE: Coding = {
  system: SNOMED,
  code: ProviderRole.PATIENT_NAVIGATOR,
  display: PROVIDER_ROLE_LABELS.PATIENT_NAVIGATOR,
};

export const PHYSICIAN_PROVIDER_ROLE: Coding = {
  system: SNOMED,
  code: ProviderRole.PHYSICIAN,
  display: PROVIDER_ROLE_LABELS.PHYSICIAN,
};

export const NURSE_PRACTITIONER_PROVIDER_ROLE: Coding = {
  system: SNOMED,
  code: ProviderRole.NURSE_PRACTITIONER,
  display: PROVIDER_ROLE_LABELS.NURSE_PRACTITIONER,
};

export const REGISTERED_DIETITIAN_PROVIDER_ROLE: Coding = {
  system: SNOMED,
  code: ProviderRole.REGISTERED_DIETITIAN,
  display: PROVIDER_ROLE_LABELS.REGISTERED_DIETITIAN,
};

export const BEHAVIORAL_EXPERT_PROVIDER_ROLE: Coding = {
  system: SNOMED,
  code: ProviderRole.BEHAVIORAL_EXPERT,
  display: PROVIDER_ROLE_LABELS.BEHAVIORAL_EXPERT,
};

export const CARE_MANAGER_PROVIDER_ROLE: Coding = {
  system: SNOMED,
  code: ProviderRole.CARE_MANAGER,
  display: PROVIDER_ROLE_LABELS.CARE_MANAGER,
};

export const SUPERVISING_PHYSICIAN_PROVIDER_ROLE: Coding = {
  system: SNOMED,
  code: ProviderRole.SUPERVISING_PHYSICIAN,
  display: PROVIDER_ROLE_LABELS.SUPERVISING_PHYSICIAN,
};

export const PATIENT_ACCESS_PROVIDER_ROLE: Coding = {
  system: SNOMED,
  code: ProviderRole.PATIENT_ACCESS,
  display: PROVIDER_ROLE_LABELS.PATIENT_ACCESS,
};

/**
 * Unlike every other provider role, this one is not SNOMED-coded: no SNOMED occupation
 * concept fits an external hospital quality auditor. See ProviderRole in constants.ts.
 */
export const EMERSON_QUALITY_AUDITOR_PROVIDER_ROLE: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: ProviderRole.EMERSON_QUALITY_AUDITOR,
  display: PROVIDER_ROLE_LABELS.EMERSON_QUALITY_AUDITOR,
};

/** Not SNOMED-coded either: no occupation concept fits. See ProviderRole in constants.ts. */
export const FINANCE_PROVIDER_ROLE: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: ProviderRole.FINANCE,
  display: PROVIDER_ROLE_LABELS.FINANCE,
};

/** Not SNOMED-coded either: no occupation concept fits. See ProviderRole in constants.ts. */
export const RESEARCH_FELLOW_PROVIDER_ROLE: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: ProviderRole.RESEARCH_FELLOW,
  display: PROVIDER_ROLE_LABELS.RESEARCH_FELLOW,
};

/** Not SNOMED-coded either: no occupation concept fits. See ProviderRole in constants.ts. */
export const MARKETING_PROVIDER_ROLE: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: ProviderRole.MARKETING,
  display: PROVIDER_ROLE_LABELS.MARKETING,
};

export const PROVIDER_ROLE_CODING_MAP: Record<ProviderRole, Coding> = {
  [ProviderRole.ADMINISTRATOR]: ADMINISTRATOR_PROVIDER_ROLE,
  [ProviderRole.PHYSICIAN]: PHYSICIAN_PROVIDER_ROLE,
  [ProviderRole.NURSE_PRACTITIONER]: NURSE_PRACTITIONER_PROVIDER_ROLE,
  [ProviderRole.REGISTERED_DIETITIAN]: REGISTERED_DIETITIAN_PROVIDER_ROLE,
  [ProviderRole.BEHAVIORAL_EXPERT]: BEHAVIORAL_EXPERT_PROVIDER_ROLE,
  [ProviderRole.PATIENT_COORDINATOR]: PATIENT_COORDINATOR_PROVIDER_ROLE,
  [ProviderRole.PATIENT_NAVIGATOR]: PATIENT_NAVIGATOR_PROVIDER_ROLE,
  [ProviderRole.CARE_MANAGER]: CARE_MANAGER_PROVIDER_ROLE,
  [ProviderRole.SUPERVISING_PHYSICIAN]: SUPERVISING_PHYSICIAN_PROVIDER_ROLE,
  [ProviderRole.PATIENT_ACCESS]: PATIENT_ACCESS_PROVIDER_ROLE,
  [ProviderRole.EMERSON_QUALITY_AUDITOR]: EMERSON_QUALITY_AUDITOR_PROVIDER_ROLE,
  [ProviderRole.FINANCE]: FINANCE_PROVIDER_ROLE,
  [ProviderRole.RESEARCH_FELLOW]: RESEARCH_FELLOW_PROVIDER_ROLE,
  [ProviderRole.MARKETING]: MARKETING_PROVIDER_ROLE,
};

export const HEADQUARTER_LOCATION_TYPE: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: LocationType.HEADQUARTERS,
  display: 'Headquarters',
};

export const PROCEDURE_INSTITUTION_LOCATION_TYPE: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: LocationType.PROCEDURE_INSTITUTION,
  display: 'Weight care center',
};

export const BARIATRIC_PRE_PROCEDURE_STAGE_CODING: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: BariatricProcedureStage.PRE_PROCEDURE,
  display: 'Pre-procedure',
};

export const BARIATRIC_POST_PROCEDURE_STAGE_CODING: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: BariatricProcedureStage.POST_PROCEDURE,
  display: 'Post-procedure',
};

export const BARIATRIC_UNKNOWN_STAGE_CODING: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: BariatricProcedureStage.UNKNOWN,
  display: 'Unknown',
};

export const INCOMING_COMMUNICATION_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: CommunicationDirection.INCOMING,
  display: 'Incoming',
};

export const OUTGOING_COMMUNICATION_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: CommunicationDirection.OUTGOING,
  display: 'Outgoing',
};

export const SMS_MESSAGE_COMMUNICATION_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: CommunicationCategory.SMS_MESSAGE,
  display: 'SMS',
};

/**
 * `Communication.reasonCode` for an automated SMS (BAR-2656). A plain code in our own system — the
 * display is deliberately human-readable because it is what shows on the Orbit timeline card.
 */
export const smsReasonCodeableConcept = (reason: SmsReason): CodeableConcept => ({
  coding: [{ system: SMS_REASON_SYSTEM, code: reason, display: SMS_REASON_DISPLAYS[reason] }],
});

/** The `SmsReason` an SMS Communication carries; undefined for a message a human typed. */
export const getSmsReason = (communication: Communication): SmsReason | undefined =>
  communication.reasonCode
    ?.flatMap((concept) => concept.coding ?? [])
    .find((coding) => coding.system === SMS_REASON_SYSTEM)?.code as SmsReason | undefined;

const SMS_REASON_DISPLAYS: Record<SmsReason, string> = {
  [SmsReason.REMINDER_24H]: 'Appointment reminder (1 day)',
  [SmsReason.REMINDER_1H]: 'Appointment reminder (1 hour)',
  [SmsReason.REMINDER_3H]: 'Appointment reminder (3 hours, unconfirmed)',
  [SmsReason.REMINDER_PROCEDURE_24H]: 'Procedure reminder (1 day)',
  [SmsReason.REMINDER_15M]: 'Appointment reminder (15 minutes)',
  [SmsReason.REMINDER_START]: 'Appointment starting now',
  [SmsReason.NO_SHOW]: 'Missed appointment follow-up',
  [SmsReason.BOOKING_CONFIRMATION]: 'Booking confirmation',
  [SmsReason.CONFIRMATION_ACK]: 'Confirmation acknowledgement',
  [SmsReason.DECLINE_ACK]: 'Decline acknowledgement',
};

export const PHONE_CALL_COMMUNICATION_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: CommunicationCategory.PHONE_CALL,
  display: 'Phone call',
};

export const PARTNER_CALL_COMMUNICATION_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: CommunicationCategory.PARTNER_CALL,
  display: 'Partner call',
};

export const LAB_ORDER_COMMUNICATION_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: CommunicationCategory.LAB_ORDER,
  display: 'Lab order',
};

export const ENCOUNTER_COMMUNICATION_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: CommunicationCategory.ENCOUNTER,
  display: 'Chart',
};

export const VIRTUAL_RECEPTIONIST_FORWARD_COMMUNICATION_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: CommunicationCategory.VIRTUAL_RECEPTIONIST_FORWARD,
  display: 'Forwarded to answering service',
};

export const SALES_TEAM_FORWARD_COMMUNICATION_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: CommunicationCategory.SALES_TEAM_FORWARD,
  display: 'Forwarded to CRM',
};

export const FORWARD_COMMUNICATION_CODING_MAP: Record<CommunicationForwardCategory, Coding> = {
  [CommunicationCategory.VIRTUAL_RECEPTIONIST_FORWARD]: VIRTUAL_RECEPTIONIST_FORWARD_COMMUNICATION_CATEGORY,
  [CommunicationCategory.SALES_TEAM_FORWARD]: SALES_TEAM_FORWARD_COMMUNICATION_CATEGORY,
};

export const TIMELINE_NOTE_COMMUNICATION_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: CommunicationCategory.TIMELINE_NOTE,
  display: 'Note',
};

export const CHAT_COMMUNICATION_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: CommunicationCategory.CHAT,
  display: 'Chat',
};

export const THREAD_COMMUNICATION_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: CommunicationCategory.THREAD,
  display: 'Chat thread',
};

export const STANDALONE_NOTE_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: CommunicationCategory.STANDALONE_NOTE,
  display: 'Note',
};

/**
 * Patient Access intro-call chart note. Distinct from STANDALONE_NOTE so the card
 * is addressable in queries, timeline filters and the access policy — a PAC rep
 * may write these and nothing else.
 */
export const PAC_INTRO_CALL_NOTE_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: CommunicationCategory.PAC_INTRO_CALL_NOTE,
  display: 'Intro call',
};

/**
 * Patient Access warm hand-off chart note — the call after the NP consult that
 * turns a completed consult into an agreed procedure date and a payment.
 * Distinct from the intro-call note so the two cards are separately
 * addressable in queries and in the access policy.
 */
export const PAC_WARM_HANDOFF_NOTE_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: CommunicationCategory.PAC_WARM_HANDOFF_NOTE,
  display: 'Warm hand-off',
};

/**
 * Marks the Provenance that records a Patient Access call being saved to the chart.
 *
 * The completion event lives on a Provenance rather than on the note because the
 * note carries no completion timestamp: `sent` is the call time and
 * `meta.lastUpdated` moves on every autosave. Same shape as the `SIGN` Provenance
 * that `sign-encounter` writes for a signed chart.
 */
export const PAC_CALL_COMPLETED_REASON: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: ProvenanceReason.PAC_CALL_COMPLETED,
  display: 'Care Intake call completed',
};

/** Marks the Provenance written for each edit of the patient's insurance card (BAR-2917). */
export const INSURANCE_COVERAGE_UPDATED_REASON: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: ProvenanceReason.INSURANCE_COVERAGE_UPDATED,
  display: 'Insurance updated',
};

/** Marks the Provenance written for each Patient profile edit from the PN consultation card (BAR-2928). */
export const PATIENT_PROFILE_UPDATED_REASON: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: ProvenanceReason.PATIENT_PROFILE_UPDATED,
  display: 'Patient profile updated',
};

/* Insurance card codings, mirroring bariendo-be's `coverage.constants.ts` and
 * `insurance-payer.constants.ts` — keep in sync. See `insurance-coverage.ts`. */

/**
 * `Organization.type` of a payer on a state's insurer list, HL7 organization-type `ins`, as
 * bariendo-be seeds them (BAR-2894) and the insurer picker must query. Not the same as
 * `INSURANCE_ORGANIZATION_CODING` (SNOMED), which types partner Organizations on the Partners page.
 */
export const INSURER_ORGANIZATION_TYPE: Coding = {
  system: ORGANIZATION_TYPE_CODESYSTEM,
  code: INSURER_ORGANIZATION_TYPE_CODE,
  display: 'Insurance Company',
};

/** `Coverage.class[].type` for the group number. */
export const GROUP_CLASS_CODING: Coding = {
  system: COVERAGE_CLASS_CODESYSTEM,
  code: GROUP_CLASS_CODE,
};

/**
 * `RelatedPerson.relationship` for a policyholder: the policyholder role, then who they are to
 * the patient. Read the other way round from the subscriber relationship: a patient who is the
 * policyholder's `child` has a parent as policyholder. `other` names no family role.
 */
export const POLICYHOLDER_ROLES: Record<Exclude<SubscriberRelationship, 'self'>, Coding[]> = {
  spouse: [
    { system: ROLE_CODE_CODESYSTEM, code: 'POLHOLD' },
    { system: ROLE_CODE_CODESYSTEM, code: 'SPS' },
  ],
  child: [
    { system: ROLE_CODE_CODESYSTEM, code: 'POLHOLD' },
    { system: ROLE_CODE_CODESYSTEM, code: 'PRN' },
  ],
  other: [{ system: ROLE_CODE_CODESYSTEM, code: 'POLHOLD' }],
};

export const FAX_INBOUND_COMMUNICATION_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: CommunicationCategory.INBOUND,
};

export const FAX_MESSAGE_COMMUNICATION_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: CommunicationCategory.FAX_MESSAGE,
  display: 'Fax',
};

export const COMMUNICATION_CATEGORY_CODING_MAP: Record<CommunicationCategory, Coding> = {
  [CommunicationCategory.INBOUND]: FAX_INBOUND_COMMUNICATION_CATEGORY,
  [CommunicationCategory.PHONE_CALL]: PHONE_CALL_COMMUNICATION_CATEGORY,
  [CommunicationCategory.SMS_MESSAGE]: SMS_MESSAGE_COMMUNICATION_CATEGORY,
  [CommunicationCategory.TIMELINE_NOTE]: TIMELINE_NOTE_COMMUNICATION_CATEGORY,
  [CommunicationCategory.STANDALONE_NOTE]: STANDALONE_NOTE_CATEGORY,
  [CommunicationCategory.CHAT]: CHAT_COMMUNICATION_CATEGORY,
  [CommunicationCategory.THREAD]: THREAD_COMMUNICATION_CATEGORY,
  [CommunicationCategory.VIRTUAL_RECEPTIONIST_FORWARD]: VIRTUAL_RECEPTIONIST_FORWARD_COMMUNICATION_CATEGORY,
  [CommunicationCategory.SALES_TEAM_FORWARD]: SALES_TEAM_FORWARD_COMMUNICATION_CATEGORY,
  [CommunicationCategory.PARTNER_CALL]: PARTNER_CALL_COMMUNICATION_CATEGORY,
  [CommunicationCategory.LAB_ORDER]: LAB_ORDER_COMMUNICATION_CATEGORY,
  [CommunicationCategory.ENCOUNTER]: ENCOUNTER_COMMUNICATION_CATEGORY,
  [CommunicationCategory.FAX_MESSAGE]: FAX_MESSAGE_COMMUNICATION_CATEGORY,
  [CommunicationCategory.PAC_INTRO_CALL_NOTE]: PAC_INTRO_CALL_NOTE_CATEGORY,
  [CommunicationCategory.PAC_WARM_HANDOFF_NOTE]: PAC_WARM_HANDOFF_NOTE_CATEGORY,
};

export const PHONE_CALL_COMMUNICATION_MEDIUM: Coding = {
  system: PARTICIPATION_MODE_CODESYSTEM,
  code: CommunicationMedium.PHONE_CALL,
  display: 'phone',
};

export const VIDEO_CALL_COMMUNICATION_MEDIUM: Coding = {
  system: PARTICIPATION_MODE_CODESYSTEM,
  code: CommunicationMedium.VIDEO_CALL,
  display: 'video call',
};

export const SMS_COMMUNICATION_MEDIUM: Coding = {
  system: PARTICIPATION_MODE_CODESYSTEM,
  code: CommunicationMedium.SMS,
  display: 'SMS',
};

export const FACILITY_ORGANIZATION_CODING: Coding = {
  system: SNOMED,
  code: OrganizationType.FACILITY,
  display: 'Facility',
};

export const PHARMACY_ORGANIZATION_CODING: Coding = {
  system: SNOMED,
  code: OrganizationType.PHARMACY,
  display: 'Pharmacy',
};

export const LABORATORY_ORGANIZATION_CODING: Coding = {
  system: SNOMED,
  code: OrganizationType.LABORATORY,
  display: 'Laboratory',
};

export const SUPPLIER_ORGANIZATION_CODING: Coding = {
  system: SNOMED,
  code: OrganizationType.SUPPLIER,
  display: 'Supplier',
};

/** A partner Organization typed Insurance on the Partners page. Insurers on a state's list use `INSURER_ORGANIZATION_TYPE`. */
export const INSURANCE_ORGANIZATION_CODING: Coding = {
  system: SNOMED,
  code: OrganizationType.INSURANCE,
  display: 'Insurance',
};

export const EXTERNAL_PROVIDER_ORGANIZATION_CODING: Coding = {
  system: SNOMED,
  code: OrganizationType.EXTERNAL_PROVIDER,
  display: 'External Provider',
};

export const IV_HYDRATION_PARTNER_ORGANIZATION_CODING: Coding = {
  system: SNOMED,
  code: OrganizationType.IV_HYDRATION_PARTNER,
  display: 'IV Hydration Partner',
};

/** An anesthesia group we pay per procedure (BAR-3042). */
export const ANESTHESIA_ORGANIZATION_CODING: Coding = {
  system: SNOMED,
  code: OrganizationType.ANESTHESIA,
  display: 'Anesthesia',
};

export const ORGANIZATION_TYPE_CODING_MAP: Record<OrganizationType, Coding> = {
  [OrganizationType.FACILITY]: FACILITY_ORGANIZATION_CODING,
  [OrganizationType.PHARMACY]: PHARMACY_ORGANIZATION_CODING,
  [OrganizationType.LABORATORY]: LABORATORY_ORGANIZATION_CODING,
  [OrganizationType.SUPPLIER]: SUPPLIER_ORGANIZATION_CODING,
  [OrganizationType.INSURANCE]: INSURANCE_ORGANIZATION_CODING,
  [OrganizationType.EXTERNAL_PROVIDER]: EXTERNAL_PROVIDER_ORGANIZATION_CODING,
  [OrganizationType.IV_HYDRATION_PARTNER]: IV_HYDRATION_PARTNER_ORGANIZATION_CODING,
  [OrganizationType.ANESTHESIA]: ANESTHESIA_ORGANIZATION_CODING,
};

export const MMS_COMMUNICATION_MEDIUM: Coding = {
  system: PARTICIPATION_MODE_CODESYSTEM,
  code: CommunicationMedium.MMS,
  display: 'MMS',
};

export const APP_COMMUNICATION_MEDIUM: Coding = {
  system: PARTICIPATION_MODE_CODESYSTEM,
  code: CommunicationMedium.APP,
  display: 'app message',
};

export const EMAIL_COMMUNICATION_MEDIUM: Coding = {
  system: PARTICIPATION_MODE_CODESYSTEM,
  code: CommunicationMedium.EMAIL,
  display: 'email',
};

export const FAX_COMMUNICATION_MEDIUM: Coding = {
  system: PARTICIPATION_MODE_CODESYSTEM,
  code: CommunicationMedium.FAX,
  display: 'telefax',
};

export const CHAT_COMMUNICATION_MEDIUM: Coding = {
  system: PARTICIPATION_MODE_CODESYSTEM,
  code: CommunicationMedium.CHAT,
  display: 'chat',
};

export const LOGISTICAL_THREAD_TOPIC: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: CommunicationThreadTopic.LOGISTICAL,
  display: COMMUNICATION_THREAD_TOPIC_PATIENT_LABELS[CommunicationThreadTopic.LOGISTICAL],
};

export const CLINICAL_THREAD_TOPIC: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: CommunicationThreadTopic.CLINICAL,
  display: COMMUNICATION_THREAD_TOPIC_PATIENT_LABELS[CommunicationThreadTopic.CLINICAL],
};

export const NUTRITION_THREAD_TOPIC: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: CommunicationThreadTopic.NUTRITION,
  display: COMMUNICATION_THREAD_TOPIC_PATIENT_LABELS[CommunicationThreadTopic.NUTRITION],
};

export const CALLABLE_ROLE: CodeableConcept = {
  coding: [
    {
      system: BARIENDO_CT_SYSTEM,
      code: 'callable',
      display: 'Callable',
    },
  ],
  id: 'callable',
  text: 'Callable',
};

export const LOW_CONFIDENTIALITY_LEVEL_CODING: Coding = {
  system: CONFIDENTIALITY_CODESYSTEM,
  code: ConfidentialityLevel.LOW,
  display: 'Low',
};

export const MODERATE_CONFIDENTIALITY_LEVEL_CODING: Coding = {
  system: CONFIDENTIALITY_CODESYSTEM,
  code: ConfidentialityLevel.MODERATE,
  display: 'Moderate',
};

export const NORMAL_CONFIDENTIALITY_LEVEL_CODING: Coding = {
  system: CONFIDENTIALITY_CODESYSTEM,
  code: ConfidentialityLevel.NORMAL,
  display: 'Normal',
};

export const RESTRICTED_CONFIDENTIALITY_LEVEL_CODING: Coding = {
  system: CONFIDENTIALITY_CODESYSTEM,
  code: ConfidentialityLevel.RESTRICTED,
  display: 'Restricted',
};

export const UNRESTRICTED_CONFIDENTIALITY_LEVEL_CODING: Coding = {
  system: CONFIDENTIALITY_CODESYSTEM,
  code: ConfidentialityLevel.UNRESTRICTED,
  display: 'Unrestricted',
};

export const VERY_RESTRICTED_CONFIDENTIALITY_LEVEL_CODING: Coding = {
  system: CONFIDENTIALITY_CODESYSTEM,
  code: ConfidentialityLevel.VERY_RESTRICTED,
  display: 'Very restricted',
};

/**
 * Marks a Provenance as payment attribution: who created a treatment order or
 * hand-keyed a settlement (BAR-2614). Policies grant writes on this tag because
 * Medplum checks writes with a matcher that has no `:identifier` support.
 */
export const PAYMENT_ATTRIBUTION_PROVENANCE_TAG: Coding = {
  system: 'https://bariendo.com/provenance-kind',
  code: 'payment-attribution',
};

export const CONFIDENTIALITY_CODINGS_MAP: Record<ConfidentialityLevel, Coding> = {
  [ConfidentialityLevel.LOW]: LOW_CONFIDENTIALITY_LEVEL_CODING,
  [ConfidentialityLevel.MODERATE]: MODERATE_CONFIDENTIALITY_LEVEL_CODING,
  [ConfidentialityLevel.NORMAL]: NORMAL_CONFIDENTIALITY_LEVEL_CODING,
  [ConfidentialityLevel.RESTRICTED]: RESTRICTED_CONFIDENTIALITY_LEVEL_CODING,
  [ConfidentialityLevel.UNRESTRICTED]: UNRESTRICTED_CONFIDENTIALITY_LEVEL_CODING,
  [ConfidentialityLevel.VERY_RESTRICTED]: VERY_RESTRICTED_CONFIDENTIALITY_LEVEL_CODING,
};

export const RESOURCE_ASSIGNMENT_TASK_GROUP_IDENTIFIER: Identifier = {
  system: BARIENDO_CT_SYSTEM,
  value: TaskGroupIdentifier.RESOURCE_ASSIGNMENT,
};

export const CONVERSATION_LEAD_TASK_GROUP_IDENTIFIER: Identifier = {
  system: BARIENDO_CT_SYSTEM,
  value: TaskGroupIdentifier.CONVERSATION_LEAD,
};

export const PROVIDER_MENTION_TASK_GROUP_IDENTIFIER: Identifier = {
  system: BARIENDO_CT_SYSTEM,
  value: TaskGroupIdentifier.PROVIDER_MENTION,
};

export const ENCOUNTER_TASK_GROUP_IDENTIFIER: Identifier = {
  system: BARIENDO_CT_SYSTEM,
  value: TaskGroupIdentifier.ENCOUNTER,
};

export const ORBIT_PROVIDER_TASK_GROUP_IDENTIFIER: Identifier = {
  system: BARIENDO_CT_SYSTEM,
  value: TaskGroupIdentifier.ORBIT_PROVIDER_TASK,
};

export const ORBIT_PROVIDER_TASK_CODING: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: TaskGroupIdentifier.ORBIT_PROVIDER_TASK,
};

// Flag categories
export const PROVIDER_REAL_TIME_AVAILABILITY_FLAG_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: FlagCategory.PROVIDER_REAL_TIME_AVAILABILITY,
  display: 'Provider Real-Time Availability',
};

export const PROVIDER_IN_CALL_FLAG_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: FlagCategory.PROVIDER_IN_CALL,
  display: 'Provider In Call',
};

export const PAC_LEAD_STATUS_FLAG_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: FlagCategory.PAC_LEAD_STATUS,
  display: 'PAC lead status',
};

export const FLAG_CATEGORY_CODING_MAP: Record<FlagCategory, Coding> = {
  [FlagCategory.PROVIDER_REAL_TIME_AVAILABILITY]: PROVIDER_REAL_TIME_AVAILABILITY_FLAG_CATEGORY,
  [FlagCategory.PROVIDER_IN_CALL]: PROVIDER_IN_CALL_FLAG_CATEGORY,
  [FlagCategory.PAC_LEAD_STATUS]: PAC_LEAD_STATUS_FLAG_CATEGORY,
};

export const PROVIDER_ONLINE_AVAILABILITY_STATUS_CODING: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: ProviderAvailabilityStatus.ONLINE,
  display: 'Online',
};

export const PROVIDER_DO_NOT_DISTURB_AVAILABILITY_STATUS_CODING: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: ProviderAvailabilityStatus.DO_NOT_DISTURB,
  display: 'Do not disturb',
};

export const PROVIDER_AVAILABILITY_STATUS_CODING_MAP: Record<ProviderAvailabilityStatus, Coding> = {
  [ProviderAvailabilityStatus.ONLINE]: PROVIDER_ONLINE_AVAILABILITY_STATUS_CODING,
  [ProviderAvailabilityStatus.DO_NOT_DISTURB]: PROVIDER_DO_NOT_DISTURB_AVAILABILITY_STATUS_CODING,
};

// LOINC codes for clinical observations
export const BODY_WEIGHT_LOINC_CODING: Coding = {
  system: 'http://loinc.org',
  code: '29463-7',
  display: 'Body weight',
};

export const BODY_HEIGHT_LOINC_CODING: Coding = {
  system: 'http://loinc.org',
  code: '8302-2',
  display: 'Body height',
};

export const BODY_MASS_INDEX_LOINC_CODING: Coding = {
  system: 'http://loinc.org',
  code: '39156-5',
  display: 'Body mass index (BMI) [Ratio]',
};

export const CLINICAL_PDF_REPORT_CODING: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: 'ClinicalPDFReport1',
  display: 'Clinical PDF Report',
};

export const FAX_COMMUNICATION_CATEGORY_OUTBOUND = {
  system: 'http://medplum.com/fhir/CodeSystem/fax-direction',
  code: 'outbound',
};

/** Medplum eFax inbound category (distinct from Bariendo `CommunicationCategory.INBOUND` token). */
export const FAX_COMMUNICATION_CATEGORY_INBOUND = {
  system: FAX_COMMUNICATION_CATEGORY_OUTBOUND.system,
  code: 'inbound',
};

// Schedule category codings for Schedule.serviceCategory
export const CALL_AVAILABILITY_SCHEDULE_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: ScheduleCategory.CALL_AVAILABILITY,
  display: 'Call Availability',
};

export const APPOINTMENT_AVAILABILITY_SCHEDULE_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: ScheduleCategory.APPOINTMENT_AVAILABILITY,
  display: 'Appointment Availability',
};

export const IVR_BUSINESS_HOURS_SCHEDULE_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: ScheduleCategory.IVR_BUSINESS_HOURS,
  display: 'IVR Business Hours',
};

export const ON_CALL_NP_SCHEDULE_CATEGORY: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: ScheduleCategory.ON_CALL_NP,
  display: 'On-Call NP',
};

export const SCHEDULE_CATEGORY_CODING_MAP: Record<ScheduleCategory, Coding> = {
  [ScheduleCategory.CALL_AVAILABILITY]: CALL_AVAILABILITY_SCHEDULE_CATEGORY,
  [ScheduleCategory.APPOINTMENT_AVAILABILITY]: APPOINTMENT_AVAILABILITY_SCHEDULE_CATEGORY,
  [ScheduleCategory.IVR_BUSINESS_HOURS]: IVR_BUSINESS_HOURS_SCHEDULE_CATEGORY,
  [ScheduleCategory.ON_CALL_NP]: ON_CALL_NP_SCHEDULE_CATEGORY,
};

// Encounter.class: https://hl7.org/fhir/R4/v3/ActEncounterCode/vs.html
export const AMBULATORY_ENCOUNTER_CLASS_CODING: Coding = {
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
  code: 'AMB', // In-person
  display: 'ambulatory',
};

export const VIRTUAL_ENCOUNTER_CLASS_CODING: Coding = {
  system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
  code: 'VR',
  display: 'virtual',
};

// Appointment.participant.type: https://hl7.org/fhir/R4/appointment-definitions.html#Appointment.participant.type
export const APPOINTMENT_PARTICIPANT_TYPE_ADMITTER_CODING: Coding = {
  system: 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType',
  code: 'ADM',
  display: 'admitter',
};

// Medical Record identifier type coding
export const MR_CODING: Coding = {
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
  code: 'MR',
  display: 'Medical record number',
};

export const SUTURE_COUNT_CODING: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: 'suture-count',
  display: 'Number of sutures',
};

export const STITCH_COUNT_CODING: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: 'stitch-count',
  display: 'Number of stitches',
};

export const SUTURE_PATTERN_CODING: Coding = {
  system: BARIENDO_CT_SYSTEM,
  code: 'suture-pattern',
  display: 'Suture pattern used',
};

export const RESCHEDULED_CODING: Coding = {
  system: SNOMED,
  code: '58334001',
  display: 'Rescheduled',
};

export const IVR_TEMPLATE_BASIC_CODING: Coding = {
  system: IVR_BASIC_SYSTEM,
  code: 'ivr-template',
  display: 'IVR Template',
};

export const IVR_SYSTEM_CONFIG_BASIC_CODING: Coding = {
  system: IVR_BASIC_SYSTEM,
  code: 'ivr-system-config',
  display: 'IVR System Configuration',
};
